import React from 'react';
import { useNavigate } from 'react-router-dom';
import InstallerLayout from '../components/installer/InstallerLayout';
import JobCard from '../components/installer/JobCard';
import { useInstallerJobs, groupJobsByDate } from '../hooks/useInstallerJobs';
import { useInstaller } from '../contexts/InstallerContext';

export default function InstallerPortal() {
  const navigate = useNavigate();
  const jobs = useInstallerJobs();
  const grouped = groupJobsByDate(jobs);
  const installer = useInstaller();

  const handleJobClick = (job) => {
    navigate('/installer/job/' + job.id, { state: { job } });
  };

  const Section = ({ title, jobs, icon, isOverdue = false }) => {
    if (jobs.length === 0) return null;

    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          fontSize: '16px',
          fontWeight: '700',
          color: isOverdue ? '#dc2626' : '#8B4513',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>{icon}</span>
          <span>{title}</span>
        </div>

        {isOverdue && (
          <div style={{
            background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
            border: '2px solid #F59E0B',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)'
          }}>
            <div style={{ fontSize: '24px' }}>Warning</div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#92400E',
                marginBottom: '2px'
              }}>
                Please Mark These Jobs Complete
              </div>
              <div style={{
                fontSize: '13px',
                color: '#78350F'
              }}>
                These jobs were scheduled in the past. Tap each job to mark complete or report any issues.
              </div>
            </div>
          </div>
        )}

        {jobs.map((job, index) => (
          <JobCard 
            key={`${job.id}-${job._displayDate || index}`}
            job={job} 
            onClick={handleJobClick}
            isOverdue={isOverdue}
          />
        ))}
      </div>
    );
  };

  const getTodayLabel = () => {
    const today = new Date();
    const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });
    return `Today - ${dayName} (${grouped.today.length} job${grouped.today.length !== 1 ? 's' : ''})`;
  };

  const getTomorrowLabel = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayName = tomorrow.toLocaleDateString('en-US', { weekday: 'long' });
    return `Tomorrow - ${dayName} (${grouped.tomorrow.length} job${grouped.tomorrow.length !== 1 ? 's' : ''})`;
  };

  const BrandingCard = () => (
    <div style={{
      position: 'relative',
      borderRadius: '24px',
      padding: '32px 24px',
      marginBottom: '28px',
      overflow: 'hidden',
      textAlign: 'center'
    }}>
      <div style={{
        position: 'absolute',
        top: '-20px',
        left: '-20px',
        right: '-20px',
        bottom: '-20px',
        backgroundImage: 'url(/brand/shop-bg.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'blur(12px)',
        transform: 'scale(1.1)',
        zIndex: 1,
        opacity: 0.3
      }} />

      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        zIndex: 2
      }} />

      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.08) 0%, rgba(160, 82, 45, 0.05) 100%)',
        zIndex: 3
      }} />

      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        border: '1px solid rgba(255, 255, 255, 0.6)',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(139, 69, 19, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
        zIndex: 4,
        pointerEvents: 'none'
      }} />

      <div style={{ position: 'relative', zIndex: 5 }}>
        <img 
          src="/brand/twp-logo.png" 
          alt="The Window Place LLC"
          style={{
            maxWidth: '200px',
            height: 'auto',
            marginBottom: '20px',
            filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))'
          }}
        />
        <div style={{
          fontSize: '22px',
          fontWeight: '800',
          color: '#8B4513',
          marginBottom: '6px',
          textShadow: '0 2px 4px rgba(255, 255, 255, 0.5)',
          letterSpacing: '-0.5px'
        }}>
          The Window Place LLC
        </div>
        <div style={{
          fontSize: '15px',
          fontWeight: '600',
          color: '#A0522D',
          marginBottom: '16px',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          opacity: 0.9
        }}>
          Installer Portal
        </div>
        <div style={{
          display: 'inline-block',
          padding: '10px 20px',
          background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.1) 0%, rgba(160, 82, 45, 0.08) 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(139, 69, 19, 0.2)',
          fontSize: '16px',
          color: '#2b2b2b',
          fontWeight: '600',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          Welcome, {installer.name || 'Installer'}!
        </div>
      </div>
    </div>
  );

  if (jobs.length === 0) {
    return (
      <InstallerLayout showSplash={true}>
        <BrandingCard />
        <div style={{
          background: 'white',
          padding: '40px 20px',
          borderRadius: '16px',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <h2 style={{ 
            color: '#8B4513', 
            marginBottom: '8px',
            fontSize: '20px',
            fontWeight: '700'
          }}>
            No Jobs Scheduled
          </h2>
          <p style={{ 
            color: '#666', 
            fontSize: '15px',
            margin: 0
          }}>
            You don't have any jobs assigned yet.
          </p>
        </div>
      </InstallerLayout>
    );
  }

  return (
    <InstallerLayout showSplash={true}>
      <BrandingCard />
      
      <Section 
        title={`Overdue - Mark Complete (${grouped.overdue?.length || 0} job${(grouped.overdue?.length || 0) !== 1 ? 's' : ''})`}
        jobs={grouped.overdue || []} 
        icon="!"
        isOverdue={true}
      />

      <Section 
        title={getTodayLabel()} 
        jobs={grouped.today} 
        icon="Today" 
      />
      <Section 
        title={getTomorrowLabel()} 
        jobs={grouped.tomorrow} 
        icon="Tomorrow" 
      />
      <Section 
        title={`This Week (${grouped.thisWeek.length} job${grouped.thisWeek.length !== 1 ? 's' : ''})`} 
        jobs={grouped.thisWeek} 
        icon="Week" 
      />
      <Section 
        title={`Upcoming (${grouped.upcoming.length} job${grouped.upcoming.length !== 1 ? 's' : ''})`} 
        jobs={grouped.upcoming} 
        icon="Soon" 
      />
    </InstallerLayout>
  );
}